import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  AppBar, Toolbar, Box, Button, IconButton,
  Avatar, Menu, MenuItem, Divider, Drawer, List, ListItemButton,
  ListItemIcon, ListItemText, useMediaQuery, useTheme, Tooltip, Typography,
} from "@mui/material";
import DashboardIcon         from "@mui/icons-material/Dashboard";
import RssFeedIcon           from "@mui/icons-material/RssFeed";
import ManageSearchIcon      from "@mui/icons-material/ManageSearch";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import HealthAndSafetyIcon   from "@mui/icons-material/HealthAndSafety";
import StorageIcon           from "@mui/icons-material/Storage";
import ManageAccountsIcon    from "@mui/icons-material/ManageAccounts";
import GridViewIcon          from "@mui/icons-material/GridView";
import BusinessIcon          from "@mui/icons-material/Business";
import FilterAltIcon         from "@mui/icons-material/FilterAlt";
import TuneIcon              from "@mui/icons-material/Tune";
import MenuIcon              from "@mui/icons-material/Menu";
import LogoutIcon            from "@mui/icons-material/Logout";
import PersonIcon            from "@mui/icons-material/Person";
import ContrastIcon          from "@mui/icons-material/Contrast";
import { signout, isAutheticated } from "../auth/helper";
import { useThemeVariant } from "../ThemeContext";

const NAV_ITEMS = [
  { label: "Dashboard",  path: "/dashboard", icon: <DashboardIcon fontSize="small" />,          adminOnly: true,  pageKey: "dashboard" },
  { label: "Feed Items", path: "/feeds",      icon: <RssFeedIcon fontSize="small" /> },
  { label: "Search",     path: "/search",     icon: <ManageSearchIcon fontSize="small" /> },
  { label: "Subadmins",  path: "/subadmins",  icon: <AdminPanelSettingsIcon fontSize="small" />, adminOnly: true,  pageKey: "subadmins" },
  { label: "Server",     path: "/server",     icon: <HealthAndSafetyIcon fontSize="small" />,    adminOnly: true,  pageKey: "server" },
  { label: "Redis",      path: "/redis",      icon: <StorageIcon fontSize="small" />,            adminOnly: true,  pageKey: "redis" },
  { label: "Companies",  path: "/companies",  icon: <BusinessIcon fontSize="small" />,           adminOnly: true,  pageKey: "companies" },
  { label: "Sources",       path: "/sources",       icon: <GridViewIcon fontSize="small" />,       adminOnly: true, pageKey: "sources"   },
  { label: "Login Logs",    path: "/login-logs",    icon: <ManageAccountsIcon fontSize="small" />, adminOnly: true, pageKey: "login-logs", moveToProfile: true },
  { label: "Filter Rules",  path: "/filter-rules",  icon: <FilterAltIcon fontSize="small" />,      adminOnly: true, pageKey: "sources",    moveToProfile: true },
  { label: "Feed Settings", path: "/feed-settings", icon: <TuneIcon fontSize="small" />,           adminOnly: true, pageKey: "dashboard",  moveToProfile: true },
];

const TopNav = () => {
  const history  = useHistory();
  const location = useLocation();
  const auth     = isAutheticated();
  const isAdmin  = auth?.admin?.role === 1;
  const adminName = auth?.admin?.name || "Admin";
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { variant, toggleTheme } = useThemeVariant();

  const [anchorEl,    setAnchorEl]    = useState(null);
  const [drawerOpen,  setDrawerOpen]  = useState(false);

  const navigate      = (path) => { history.push(path); setDrawerOpen(false); };
  const handleSignout = ()     => { signout(() => history.push("/login")); };
  const isActive      = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  const allowedPages = auth?.admin?.allowedPages || [];
  const visibleItems = NAV_ITEMS.filter((item) => {
    if (isAdmin && item.moveToProfile) return false;
    return !item.adminOnly || isAdmin || (item.pageKey && allowedPages.includes(item.pageKey));
  });

  const profileMenuItems = isAdmin ? NAV_ITEMS.filter((item) => item.moveToProfile) : [];

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{ bgcolor: "background.paper", borderBottom: "1px solid", borderColor: "divider", color: "text.primary" }}
      >
        <Toolbar sx={{ gap: 1, minHeight: { xs: "52px !important", xl: "60px !important" }, px: { xs: 2, md: 3, xl: 4 } }}>
          {isMobile && (
            <IconButton size="small" onClick={() => setDrawerOpen(true)} sx={{ mr: 0.5 }}>
              <MenuIcon fontSize="small" />
            </IconButton>
          )}

          {/* Brand */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: 3 }}>
            <Box
              sx={{
                width: 28, height: 28, borderRadius: "8px",
                bgcolor: "rgba(59,130,246,0.15)",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <RssFeedIcon sx={{ color: "primary.main", fontSize: 17 }} />
            </Box>
            <Typography variant="body2" fontWeight={700} sx={{ letterSpacing: "-0.2px", whiteSpace: "nowrap" }}>
              FeedCore
            </Typography>
          </Box>

          {/* Desktop nav links */}
          {!isMobile && (
            <Box sx={{
              display: "flex", gap: 0.5, flex: 1,
              overflowX: "auto", scrollbarWidth: "none", msOverflowStyle: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}>
              {visibleItems.map((item) => {
                const active = isActive(item.path);
                return (
                  <Button
                    key={item.path}
                    size="small"
                    startIcon={item.icon}
                    onClick={() => navigate(item.path)}
                    sx={{
                      px: { md: 0.8, lg: 1.2, xl: 1.6 },
                      py: { md: 0.5, xl: 0.7 },
                      fontSize: { md: "0.72rem", lg: "0.78rem", xl: "0.83rem", xxl: "0.9rem" },
                      fontWeight: active ? 600 : 400,
                      color: active ? "primary.main" : "text.secondary",
                      bgcolor: active ? "rgba(59,130,246,0.1)" : "transparent",
                      borderRadius: 1.5,
                      textTransform: "none",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      "&:hover": { bgcolor: "rgba(255,255,255,0.06)", color: "text.primary" },
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </Box>
          )}

          <Box sx={{ flex: isMobile ? 1 : 0 }} />

          {/* Theme toggle */}
          <Tooltip title={variant === "navy" ? "Switch to Black" : "Switch to Navy"}>
            <IconButton size="small" onClick={toggleTheme} sx={{ mr: 0.5, color: "text.secondary", "&:hover": { color: "text.primary" } }}>
              <ContrastIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Avatar / user menu */}
          <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ p: 0 }}>
            <Avatar sx={{ width: 30, height: 30, bgcolor: "primary.main", fontSize: "0.8rem", fontWeight: 700 }}>
              {adminName[0].toUpperCase()}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            PaperProps={{ sx: { mt: 0.5, minWidth: 170, bgcolor: "background.paper", border: "1px solid", borderColor: "divider" } }}
          >
            <Box sx={{ px: 2, py: 1.2 }}>
              <Typography variant="body2" fontWeight={600}>{adminName}</Typography>
              <Typography variant="caption" color="text.secondary">{isAdmin ? "Super Admin" : "Sub Admin"}</Typography>
            </Box>
            <Divider />
            {profileMenuItems.length > 0 && (
              <>
                {profileMenuItems.map((item) => (
                  <MenuItem key={item.path}
                    onClick={() => { setAnchorEl(null); navigate(item.path); }}
                    sx={{ gap: 1.5, fontSize: "0.85rem", color: "text.secondary" }}>
                    {item.icon} {item.label}
                  </MenuItem>
                ))}
                <Divider />
              </>
            )}
            <MenuItem onClick={() => { setAnchorEl(null); navigate("/profile"); }}
              sx={{ gap: 1.5, fontSize: "0.85rem" }}>
              <PersonIcon fontSize="small" /> Profile
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleSignout} sx={{ gap: 1.5, color: "error.main", fontSize: "0.85rem" }}>
              <LogoutIcon fontSize="small" /> Sign out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 240, pt: 1 }}>
          <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
            <RssFeedIcon sx={{ color: "primary.main", fontSize: 20 }} />
            <Typography variant="body2" fontWeight={700}>FeedCore</Typography>
          </Box>
          <Divider />
          <List dense>
            {visibleItems.map((item) => (
              <ListItemButton key={item.path} selected={isActive(item.path)} onClick={() => navigate(item.path)}>
                <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </ListItemButton>
            ))}
          </List>
          <Divider />
          <List dense>
            {profileMenuItems.map((item) => (
              <ListItemButton key={item.path} selected={isActive(item.path)} onClick={() => navigate(item.path)}>
                <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </ListItemButton>
            ))}
            <ListItemButton onClick={handleSignout} sx={{ color: "error.main" }}>
              <ListItemIcon sx={{ minWidth: 36, color: "error.main" }}><LogoutIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Sign out" primaryTypographyProps={{ fontSize: "0.875rem" }} />
            </ListItemButton>
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default TopNav;
